import { Validators } from '@biteinc/common';
import { allEnumValues, ItemHiddenState, ItemTaxationStatusHelper, MenuItemCategory, MenuItemDisplayStyle, ModPriceOptionDisplayStyle, UserRight, } from '@biteinc/enums';
import { SyncableMenuModelSchema } from './syncable_menu_model_schema.js';
export const modSchema = {
    displayName: 'modifier',
    type: 'object',
    fields: {
        ...SyncableMenuModelSchema.fields,
        isMod: {
            type: 'bool',
            isHidden: true,
            isPermanent: true,
            isReadOnly: true,
            required: true,
        },
        name: {
            type: 'shortString',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        internalName: {
            type: 'shortString',
            displayName: 'Internal Name',
            collapse: true,
            tooltip: {
                text: 'A name used to identify this mod internally. Not seen by guests.',
            },
            writeRight: UserRight.ManageMenu,
        },
        posName: {
            type: 'shortString',
            displayName: 'POS Name',
            isReadOnly: true,
            tooltip: {
                text: 'This is the default display name. The field “Display Name” will replace this name on the kiosk.',
            },
        },
        posInternalName: {
            type: 'keyString',
            isReadOnly: true,
            isHidden: true,
        },
        fulfillmentName: {
            type: 'shortString',
            displayName: 'Fulfillment Name',
            tooltip: {
                text: 'This is the name that will be displayed on the KDS for fulfillment integrations.',
            },
        },
        description: {
            type: 'longString',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        posDescription: {
            type: 'longString',
            displayName: 'POS Description',
            isReadOnly: true,
            tooltip: {
                text: 'This is the default display description. The field “Display Description” will replace this description on the kiosk.',
            },
        },
        vendorId: {
            type: 'mongoId',
            displayName: 'Vendor',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        category: {
            type: 'int',
            values: allEnumValues(MenuItemCategory),
            ui: 'radiolist',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        taxationStatus: {
            type: 'int',
            values: ItemTaxationStatusHelper.allValuesForMods(),
            displayName: 'Taxation Status',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        taxProfileId: {
            type: 'mongoId',
            displayName: 'Tax Profile',
            ui: 'dropdown',
            writeRight: UserRight.ManageMenu,
        },
        i9nTaxProfileId: {
            type: 'keyString',
            isReadOnly: true,
            isHidden: true,
        },
        badges: {
            displayName: 'Badges',
            type: 'array',
            elementType: 'object',
            fields: {
                _id: {
                    type: 'mongoId',
                    required: true,
                },
                i9nId: {
                    type: 'keyString',
                },
            },
            ui: 'checklist',
            canCreateNew: true,
            writeRight: UserRight.ManageMenu,
        },
        i9nAvailability: {
            type: 'object',
            displayName: 'Availability',
            subtype: 'MenuItemAvailability',
            isReadOnly: true,
            writeRight: UserRight.ManageMenu,
        },
        images: {
            type: 'array',
            elementType: 'object',
            elementSubtype: 'Image',
            elementUniqueIdFieldName: 'origUrl',
            maxCount: 5,
            keepAspectRatio: 'contain',
            size: {
                width: 300,
                height: 200,
            },
            jpgSize: {
                width: 600,
                height: 400,
            },
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        displayStyle: {
            type: 'int',
            values: allEnumValues(MenuItemDisplayStyle),
            displayName: 'Display Style',
            ui: 'dropdown',
            required: true,
            writeRight: UserRight.ManageMenu,
        },
        activeTimings: {
            type: 'array',
            elementType: 'object',
            collapse: true,
            displayName: 'Show Only During',
            placeholder: 'Select a Timing',
            warningHtml: 'This mod will only be shown on the menu during the times you selected. If nothing is selected, this mod will always be shown.',
            requiresTimings: true,
            fields: {
                _id: {
                    type: 'mongoId',
                    required: true,
                },
            },
            writeRight: UserRight.ManageMenu,
        },
        denormalizeModGroups: {
            type: 'bool',
            displayName: 'Create specific mod groups for this mod',
            writeRight: UserRight.ManageMenuAdvanced,
            tooltip: {
                text: 'The POS integration needs to be synced for this change to take effect. Brink/Olo/Qu/Toast only!',
                isWarning: true,
            },
        },
        syncMultiplePriceOptions: {
            type: 'bool',
            displayName: 'Sync Multiple Price Options',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'If enabled, multiple price options will be synced from the POS. Simphony only!',
                isWarning: true,
            },
        },
        usesModCodes: {
            type: 'bool',
            displayName: 'Uses Mod Codes',
            tooltip: {
                text: 'If enabled, the mod-code group will appear after the next sync. The code can be used to modify the mod on the order (Revel only).',
            },
        },
        priceOptions: {
            type: 'array',
            elementType: 'object',
            minCount: 1,
            required: true,
            ui: 'accordion',
            uiSubtype: 'PriceOptions',
            fields: {
                _id: {
                    type: 'mongoId',
                    isHidden: true,
                },
                i9nId: {
                    type: 'shortString',
                    isReadOnly: true,
                    isHidden: true,
                },
                posId: {
                    type: 'shortString',
                    isReadOnly: true,
                    isHidden: true,
                },
                price: {
                    type: 'price',
                    canBeNegative: true,
                    required: true,
                },
                barcode: {
                    type: 'keyString',
                    validationRegex: Validators.barcodeRegexString,
                    writeRight: UserRight.ManageMenu,
                },
                nutritionInfo: {
                    type: 'object',
                    displayName: 'Nutrition Info',
                    subtype: 'NutritionInfo',
                    writeRight: UserRight.ManageMenu,
                },
                hiddenState: {
                    type: 'int',
                    values: allEnumValues(ItemHiddenState),
                    isHidden: true,
                },
                i9nHiddenState: {
                    type: 'int',
                    values: allEnumValues(ItemHiddenState),
                    isHidden: true,
                    isReadOnly: true,
                },
                selectedByDefault: {
                    type: 'bool',
                    displayName: 'Selected by Default',
                    writeRight: UserRight.ManageMenu,
                },
                i9nSelectedByDefault: {
                    type: 'bool',
                    displayName: 'POS Selected by Default',
                    isReadOnly: true,
                },
                addonSetIds: {
                    type: 'array',
                    elementType: 'mongoId',
                    displayName: 'Modifier Groups',
                    ui: 'table',
                    placeholder: 'Select Modifier Group',
                    collapse: true,
                },
                i9nAddonSetIds: {
                    type: 'array',
                    elementType: 'keyString',
                    isReadOnly: true,
                    isHidden: true,
                },
                floatingModGroupIds: {
                    type: 'array',
                    elementType: 'mongoId',
                    displayName: 'Floating Mod Groups',
                    ui: 'table',
                    placeholder: 'Select Floating Modifier Group',
                    collapse: true,
                    writeRight: UserRight.ManageMenu,
                },
                name: {
                    type: 'shortString',
                    displayName: 'Price Option Name',
                    canHaveLocalizationOverrides: true,
                    writeRight: UserRight.ManageMenu,
                },
                posName: {
                    type: 'shortString',
                    displayName: 'POS Name',
                    isPermanent: true,
                    isReadOnly: true,
                    tooltip: {
                        text: 'This is the default display name. The field “Price Option Name” will replace this name on the kiosk.',
                    },
                },
                fulfillmentName: {
                    type: 'shortString',
                    displayName: 'Fulfillment Name',
                    tooltip: {
                        text: 'This is the name that will be displayed on the KDS for fulfillment integrations.',
                    },
                },
                images: {
                    type: 'array',
                    elementType: 'object',
                    elementSubtype: 'Image',
                    elementUniqueIdFieldName: 'origUrl',
                    maxCount: 5,
                    keepAspectRatio: 'contain',
                    size: {
                        width: 600,
                        height: 400,
                    },
                    jpgSize: {
                        width: 1200,
                        height: 800,
                    },
                    canHaveLocalizationOverrides: true,
                },
            },
            writeRight: UserRight.ManageMenu,
        },
        variationSource: {
            type: 'object',
            isHidden: true,
            isReadOnly: true,
            fields: {
                i9nId: {
                    type: 'keyString',
                    required: true,
                },
                magicCopyKey: {
                    type: 'keyString',
                    required: true,
                },
                i9nIdSuffix: {
                    type: 'keyString',
                    required: true,
                },
                isRoot: {
                    type: 'bool',
                },
            },
        },
        recommendationLabel: {
            type: 'shortString',
            displayName: 'Recommendation Label',
            writeRight: UserRight.ManageMenu,
            canHaveLocalizationOverrides: true,
        },
        instructions: {
            type: 'longString',
            displayName: 'Instructions to be printed on the receipt',
            canHaveLocalizationOverrides: true,
            writeRight: UserRight.ManageMenu,
        },
        prepTimeMinutes: {
            type: 'int',
            min: 0,
            max: 1440, // 24 hours
            displayName: 'Prep time (minutes)',
            writeRight: UserRight.ManageMenu,
            tooltip: {
                text: 'How long this mod takes to prepare. When the location lead time source is set to menu item prep times, the order lead time is the longest prep time in the cart. Overrides posPrepTimeMinutes.',
            },
        },
        posPrepTimeMinutes: {
            type: 'int',
            min: 0,
            max: 1440, // 24 hours
            displayName: 'POS Prep time (minutes)',
            isPermanent: true,
            isReadOnly: true,
            tooltip: {
                text: 'The prep time synced from the POS. The field "Prep time (minutes)" replaces this value when set.',
            },
        },
        multiPriceOptionDisplayStyle: {
            type: 'stringEnum',
            enum: ModPriceOptionDisplayStyle,
            displayName: 'Multiple Price Options Display Style',
            tooltip: {
                text: 'This is the display style for multiple price options. If not set, the default display style (Column) will be used. Only applicable for mods with multiple price options.',
                isWarning: true,
            },
            ui: 'dropdown',
        },
        posMultiPriceOptionDisplayStyle: {
            displayName: 'POS Multiple Price Options Display Style',
            type: 'stringEnum',
            enum: ModPriceOptionDisplayStyle,
            ui: 'dropdown',
            isPermanent: true,
            isReadOnly: true,
        },
    },
    searchFields: ['name', 'posName', 'posId', 'i9nId', 'fulfillmentName', 'description'],
};
//# sourceMappingURL=mod_schema.js.map